import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { MessageGroup } from './MessageGroup';
import { Avatar } from '../atoms/Avatar';
import { ChatWidgetRenderer } from '../widgets/ChatWidget';
import { MarkdownText } from '../molecules/MarkdownText';
import { groupMessages } from '../../utils/message-grouping';
import { ChatInput } from '../molecules/ChatInput';
import type { ChatMessage } from '../../types/coach';
import type { StreamingMessage } from '../../store/chat-atoms';

interface ChatInterfaceProps {
  messages: ChatMessage[];
  streamingMessage?: StreamingMessage | null;
  onSendMessage: (message: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  userId?: string;
  sessionId?: string;
  showToolBlocks?: boolean;
  scrollPosition?: number;
  onScrollPositionChange?: (position: number) => void;
}

export function ChatInterface({ 
  messages, 
  streamingMessage, 
  onSendMessage, 
  isLoading, 
  error, 
  userId, 
  sessionId, 
  showToolBlocks = true,
  scrollPosition = 0,
  onScrollPositionChange 
}: ChatInterfaceProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRestoringScrollRef = useRef(false);
  const previousSessionIdRef = useRef(sessionId);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };


  // Throttled scroll handler to avoid excessive updates
  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current && onScrollPositionChange) {
      const {scrollTop} = scrollContainerRef.current;
      // Clear previous timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      // Set new timeout to throttle updates
      scrollTimeoutRef.current = setTimeout(() => {
        onScrollPositionChange(scrollTop);
      }, 100);
    }
  }, [onScrollPositionChange]);

  // Restore scroll position immediately on mount or when switching threads
  // Using useLayoutEffect to apply scroll before paint
  useLayoutEffect(() => {
    // Update previous session ref
    previousSessionIdRef.current = sessionId;
    
    if (scrollContainerRef.current && scrollPosition > 0 && messages.length > 0) {
      isRestoringScrollRef.current = true;
      
      // Apply scroll position immediately without animation
      scrollContainerRef.current.style.scrollBehavior = 'auto';
      scrollContainerRef.current.scrollTop = scrollPosition;
      
      // Re-enable smooth scrolling after position is set
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.style.scrollBehavior = '';
        }
        isRestoringScrollRef.current = false;
      });
    }
  }, [sessionId, messages.length, scrollPosition]); // Only trigger on session change, messages.length and scrollPosition

  // Auto-scroll to bottom for new messages (streaming or final)
  useEffect(() => {
    // Skip auto-scroll if we're restoring scroll position
    if (isRestoringScrollRef.current) {return;}
    
    // Only auto-scroll if we're already near the bottom or this is a new conversation
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      
      if (isNearBottom || messages.length <= 1) {
        scrollToBottom();
      }
    }
  }, [messages, streamingMessage]);

  // Cleanup timeout on unmount
  useEffect(() => () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    }, []);

  const handleSendMessage = async (message: string) => {
    await onSendMessage(message);
  };

  const messageGroups = groupMessages(messages);

  return (
    <div className="flex flex-col h-full">
      {/* Messages Area */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto custom-scrollbar px-4 py-6 min-h-0"
        onScroll={handleScroll}
      >
        <div className="max-w-4xl mx-auto space-y-6">
          {messageGroups.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary-stop-1 to-primary-stop-2 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-text-primary mb-2">Start a conversation</h3>
              <p className="text-text-muted max-w-sm mx-auto">
                Ask me anything about your health, fitness, or wellness goals. I&apos;m here to help!
              </p>
            </div>
          ) : (
            <>
              {messageGroups.map((group, index) => (
                <MessageGroup
                  key={`message-group-${group.messages[0]?.id ?? index}-${index}`}
                  messages={group.messages}
                  role={group.role}
                  showAvatar={group.showAvatar}
                  showTimestamp={group.showTimestamp}
                  userId={userId}
                  sessionId={sessionId}
                  showToolBlocks={showToolBlocks}
                />
              ))}
            </>
          )}
          
          {/* Streaming Message */}
          {streamingMessage && (
            <div className="flex gap-3 fade-in">
              <div className="flex-shrink-0">
                <Avatar type="assistant" size="md" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="message-bubble assistant streaming p-4">
                  {streamingMessage.widgets.length > 0 ? (
                    <div className="space-y-3">
                      {streamingMessage.widgets.map((widget, index) => (
                        <div key={`stream-widget-${widget.type}-${index}`} className="fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                          {widget.type === 'text' ? (
                            <MarkdownText className="text-sm leading-relaxed text-text-primary">
                              {widget.text}
                            </MarkdownText>
                          ) : (
                            <ChatWidgetRenderer widget={widget} />
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center space-x-3">
                      <div className="text-sm text-text-secondary pulse-slow">
                        {streamingMessage.thinking ?? 'Coach is thinking...'}
                      </div>
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-info rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-info rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                        <div className="w-2 h-2 bg-info rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Error Message */}
          {error && (
            <div className="p-4 bg-danger-light border border-danger-border rounded-xl">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-danger mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-danger">{error}</p>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>
      
      {/* Input Area */}
      <div className="border-t border-border-light bg-gradient-to-t from-bg-primary to-bg-secondary px-4 py-4">
        <div className="max-w-4xl mx-auto">
          <ChatInput
            onSendMessage={handleSendMessage}
            isLoading={isLoading}
            placeholder="Type your message..."
          />
          <div className="mt-2 text-xs text-text-muted text-center">
            Press Enter to send • Shift+Enter for new line
          </div>
        </div>
      </div>
    </div>
  );
}